import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Grid3X3, Percent, User, ShoppingCart } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { motion } from 'framer-motion';

const BottomNavBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartCount } = useCart();

  const navItems = [
    { id: 'home', label: 'Home', icon: Home, path: '/' },
    { id: 'categories', label: 'Categories', icon: Grid3X3, path: '/categories' },
    { id: 'deals', label: 'Deals', icon: Percent, path: '/deals' },
    { id: 'account', label: 'Account', icon: User, path: '/account' },
    { id: 'cart', label: 'Cart', icon: ShoppingCart, path: '/cart', badge: cartCount },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Don't show on admin pages, checkout, or auth (keep on product detail so global nav is always reachable)
  const hiddenPaths = ['/admin', '/checkout', '/login', '/register'];
  if (hiddenPaths.some(p => location.pathname.startsWith(p))) {
    return null;
  }

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-[9999] md:hidden bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-around h-16 px-2 max-w-lg mx-auto">
          {navItems.map((item) => {
            const active = isActive(item.path);
            const Icon = item.icon;
            
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`relative flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                  active ? 'text-[#0047AB]' : 'text-slate-500'
                }`}
                data-testid={`nav-${item.id}`}
              >
                {/* Active indicator */}
                {active && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute -top-0.5 w-12 h-1 bg-[#0047AB] rounded-b-full"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
                
                {/* Icon container */}
                <div className="relative">
                  <Icon 
                    className={`w-5 h-5 transition-transform ${active ? 'scale-110' : ''}`}
                    strokeWidth={active ? 2.5 : 2}
                  />
                  
                  {/* Cart badge */}
                  {item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] flex items-center justify-center bg-[#f57224] text-white text-[10px] font-bold rounded-full px-1">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                
                {/* Label */}
                <span className={`text-[10px] mt-1 font-medium ${active ? 'font-semibold' : ''}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
        
        {/* Safe area for phones with home indicator */}
        <div className="h-safe-area-inset-bottom bg-white" />
      </nav>
    </>
  );
};

export default BottomNavBar;
